import * as Blockly from 'blockly/core';
import { type GpsDistanceData, useBlockModalGpsDistanceStore } from '@/stores/useBlockModalGpsDistanceStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 110;
const PADDING = 10;

export class FieldMapPicker extends Blockly.Field<string> {
    private location_: GpsDistanceData | null;

    static override fromJson(options: Blockly.FieldConfig & { location?: GpsDistanceData }): FieldMapPicker {
        return new FieldMapPicker(options.location ?? null);
    }

    constructor(location: GpsDistanceData | null = null) {
        super(location ? JSON.stringify(location) : '');
        this.location_ = location;
        this.SERIALIZABLE = true;
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#00BFA5';
        const fillColour = this.location_ ? colour : darken(colour);
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_) return;

        [...(this.fieldGroup_?.childNodes ?? [])].forEach((node) => {
            if (node !== this.borderRect_) node.parentNode?.removeChild(node);
        });

        const isSet = this.location_ !== null;
        const label = isSet ? `${this.location_!.lat.toFixed(4)}, ${this.location_!.lng.toFixed(4)}` : '設定した場所';

        const text = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: PADDING,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': isSet ? '15px' : '17px',
                'font-weight': isSet ? '700' : '500',
                'font-family': isSet ? 'Cantarell, sans-serif' : 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;
        text.textContent = label;

        const charWidth = isSet ? 9 : 14;
        const textWidth = label.length * charWidth + PADDING * 2;
        const width = Math.max(FIELD_MIN_WIDTH, textWidth);
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        this.applyStyle_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setLocation(data: GpsDistanceData): void {
        this.location_ = data;
        const json = JSON.stringify(data);
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', json));
        }
    }

    public getLocation(): GpsDistanceData | null {
        return this.location_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            if (!newValue) {
                this.location_ = null;
            } else {
                const parsed = JSON.parse(newValue) as Partial<GpsDistanceData>;
                this.location_ =
                    Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng)
                        ? ({ lat: parsed.lat!, lng: parsed.lng! } as GpsDistanceData)
                        : null;
            }
        } catch {
            this.location_ = null;
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return this.location_ ? JSON.stringify(this.location_) : '';
    }

    override getSize(): Blockly.utils.Size {
        return this.size_ ?? new Blockly.utils.Size(FIELD_MIN_WIDTH, FIELD_HEIGHT);
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalGpsDistanceStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef({
            setDistance: (data) => this.setLocation(data),
            getDistance: () => this.getLocation(),
        });
        openModal('gps_distance');
    }
}
